import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';

import {
  addRunThreadMessageToTaskContext,
  listRunThreadMessages,
  postRunThreadMessage,
  retryRunThreadReply,
} from '@/lib/api/generated/api';
import type {
  AddedToTaskContextView,
  ThreadMessagePage,
  ThreadMessageView,
} from '@/lib/api/generated/model';
import { taskKeys } from '../queryKeys';
import { reportThreadProblem } from './report';
import {
  classifyThreadRefusal,
  ThreadRefusalError,
  type ThreadAction,
} from './threadErrors';

const PAGE_SIZE = 50;

type ThreadCache = InfiniteData<ThreadMessagePage, string | undefined>;

/**
 * One run's thread, oldest first, a page at a time (spec 149).
 *
 * The service's first page is the newest messages and `next_before` the cursor
 * to the ones before them, the inbox's shape: a thread grows at the bottom
 * while it is read. So "the next page" here is the *earlier* one, and pages are
 * shown in reverse.
 *
 * No `refetchInterval`: the stream's `run_thread_changed` invalidates this
 * query (`isRunThreadQuery`), which re-reads the newest page first and every
 * page shown after it, so a message arriving never loses the earlier pages
 * someone opened.
 */
export function useRunThread(
  workspaceId: string,
  taskId: string,
  executionId: string,
  enabled: boolean,
) {
  const query = useInfiniteQuery({
    queryKey: taskKeys.thread(workspaceId, taskId, executionId),
    queryFn: async ({ pageParam }): Promise<ThreadMessagePage> => {
      const response = await listRunThreadMessages(
        workspaceId,
        taskId,
        executionId,
        { limit: PAGE_SIZE, before: pageParam },
      );
      if (response.status !== 200) {
        reportThreadProblem('the thread failed to load', {
          status: response.status,
          executionId,
        });
        throw new Error('The conversation could not be loaded.');
      }
      return response.data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.next_before ?? undefined,
    enabled,
    retry: false,
  });
  return {
    messages: query.data
      ? [...query.data.pages].reverse().flatMap((page) => page.items)
      : undefined,
    isPending: query.isPending,
    loadFailed: query.isError,
    isFetching: query.isFetching,
    refetch: () => void query.refetch(),
    hasEarlier: query.hasNextPage,
    showEarlier: () => void query.fetchNextPage(),
    loadingEarlier: query.isFetchingNextPage,
  };
}

/** The run a thread write is about. */
export interface RunThreadAddress {
  workspaceId: string;
  taskId: string;
  executionId: string;
}

/** Writes `message` into the cached thread: in place when it is there, else at the end. */
function withMessage(cache: ThreadCache, message: ThreadMessageView) {
  const known = cache.pages.some((page) =>
    page.items.some((item) => item.id === message.id),
  );
  return {
    ...cache,
    pages: cache.pages.map((page, index) => ({
      ...page,
      items: known
        ? page.items.map((item) => (item.id === message.id ? message : item))
        : // The newest page is the first; a new message belongs at its end.
          index === 0
          ? [...page.items, message]
          : page.items,
    })),
  };
}

/**
 * Write a stored message into the thread's cache, as `useAddTaskComment` does
 * for a comment: the response is the message as stored, so a refetch could
 * only return the same. A refetch in flight is cancelled first, or it would
 * settle after this and drop the message until the stream's next word.
 */
function useWriteMessage() {
  const queryClient = useQueryClient();
  return async (
    { workspaceId, taskId, executionId }: RunThreadAddress,
    message: ThreadMessageView,
  ) => {
    const queryKey = taskKeys.thread(workspaceId, taskId, executionId);
    await queryClient.cancelQueries({ queryKey });
    const cache = queryClient.getQueryData<ThreadCache>(queryKey);
    if (cache) {
      queryClient.setQueryData<ThreadCache>(
        queryKey,
        withMessage(cache, message),
      );
      return;
    }
    await queryClient.invalidateQueries({ queryKey });
  };
}

/**
 * A write that failed without the service refusing it — the network, or a bug —
 * is reported; a refusal was already classified (and reported if its code was
 * unknown), and the caller shows its sentence either way.
 */
function reportUnexpected(action: ThreadAction) {
  return (error: Error, { executionId }: RunThreadAddress) => {
    if (error instanceof ThreadRefusalError) return;
    reportThreadProblem(`${action} failed`, {
      executionId,
      error: String(error),
    });
  };
}

/** Ask the agent about a finished run (spec 149 FR-002): the message, and its reply queued. */
export function usePostThreadMessage() {
  const write = useWriteMessage();
  return useMutation({
    mutationFn: async ({
      workspaceId,
      taskId,
      executionId,
      body,
    }: RunThreadAddress & { body: string }): Promise<ThreadMessageView> => {
      const response = await postRunThreadMessage(
        workspaceId,
        taskId,
        executionId,
        { body },
      );
      if (response.status === 201) return response.data;
      throw new ThreadRefusalError(
        classifyThreadRefusal('post', response.data, response.status),
      );
    },
    onSuccess: (message, address) => write(address, message),
    onError: reportUnexpected('post'),
  });
}

/** Ask again for a failed or refused reply (FR-003); the failed attempt stays recorded. */
export function useRetryThreadReply() {
  const write = useWriteMessage();
  return useMutation({
    mutationFn: async ({
      workspaceId,
      taskId,
      executionId,
      messageId,
    }: RunThreadAddress & {
      messageId: string;
    }): Promise<ThreadMessageView> => {
      const response = await retryRunThreadReply(
        workspaceId,
        taskId,
        executionId,
        messageId,
      );
      if (response.status === 202) return response.data;
      throw new ThreadRefusalError(
        classifyThreadRefusal('retry', response.data, response.status),
      );
    },
    onSuccess: (message, address) => write(address, message),
    onError: reportUnexpected('retry'),
  });
}

/**
 * Copy a message into the task's Comments (FR-005), where later runs read it.
 *
 * Both the thread and the comments are re-read, rather than patched: the
 * answer names only the new comment, not when the message was added, and the
 * comment's text is the service's quote of the message. `ALREADY_ADDED` re-reads
 * them too — this tab's thread was out of date, and the link is what it lacked.
 */
export function useAddThreadMessageToTaskContext() {
  const queryClient = useQueryClient();
  const refresh = ({ workspaceId, taskId, executionId }: RunThreadAddress) =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: taskKeys.thread(workspaceId, taskId, executionId),
      }),
      queryClient.invalidateQueries({
        queryKey: taskKeys.comments(workspaceId, taskId),
      }),
    ]);
  return useMutation({
    mutationFn: async ({
      workspaceId,
      taskId,
      executionId,
      messageId,
    }: RunThreadAddress & {
      messageId: string;
    }): Promise<AddedToTaskContextView> => {
      const response = await addRunThreadMessageToTaskContext(
        workspaceId,
        taskId,
        executionId,
        messageId,
      );
      if (response.status === 201) return response.data;
      throw new ThreadRefusalError(
        classifyThreadRefusal('add', response.data, response.status),
      );
    },
    onSuccess: (_added, address) => refresh(address),
    onError: (error, address) => {
      if (
        error instanceof ThreadRefusalError &&
        error.refusal.code === 'ALREADY_ADDED'
      ) {
        void refresh(address);
        return;
      }
      reportUnexpected('add')(error, address);
    },
  });
}
